"use client";

import { type TLAssetStore, Tldraw } from "tldraw";
import "tldraw/tldraw.css";
import { useEffect, useMemo, useState } from "react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { useSync } from "@tldraw/sync";
import { useParams } from "react-router-dom";
import { Icon } from "@iconify-icon/react";
import { QRCodeCanvas } from "qrcode.react";

import { useClassroomStore } from "../store/useClassroomStore";

import { cn } from "@/lib/utils.ts";
import { axiosApiInstance } from "@/libs/axios";
import useUserStore from "@/features/auth/store/useUserStore.ts";
import envs from "@/envs.ts";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface WhiteboardProps {
  fullscreen?: boolean;
}

export default function Whiteboard({ fullscreen }: WhiteboardProps) {
  const { t } = useTranslation("classroom");
  const { lessonId, workspaceId } = useParams();
  const [isLoaded, setIsLoaded] = useState(false);
  const { user } = useUserStore();
  const { isWhiteboardFullscreen, setIsWhiteboardFullscreen } = useClassroomStore();

  const multiplayerAssets = useMemo<TLAssetStore>(
    () => ({
      async upload(_asset, file) {
        const formData = new FormData();

        formData.append("file", file);
        const { data } = await axiosApiInstance.post(
          `/workspaces/${workspaceId}/lessons/${user?.role === "student" ? "student" : "tutor"}/${lessonId}/video-room/files/upload`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          },
        );

        return { meta: _asset.meta, src: data.data.url };
      },
      resolve: (asset) => {
        return asset.props.src;
      },
    }),
    [lessonId, workspaceId],
  );
  const store = useSync({
    uri: `${envs.VITE_TLDRAW_WORKER_URL}/connect/${lessonId}`,
    assets: multiplayerAssets,
    userInfo: {
      id: user?.id ?? Math.random().toString(),
      name: user?.fullName,
    },
  });

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <motion.div
      animate={{ opacity: 1 }}
      className={cn(
        "relative z-[2] h-full w-full overflow-hidden",
        fullscreen &&
          "bg-background fixed top-0 left-0 z-[2000] h-dvh! w-screen! rounded-none! p-3 pb-10",
      )}
      exit={{ opacity: 0 }}
      initial={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* QR Code Button */}
      <Popover>
        <PopoverTrigger asChild>
          <motion.button
            aria-label={t("wbQrCode")}
            className={`bg-background/80 border-border text-accent-foreground hover:bg-background absolute ${fullscreen ? "right-2 bottom-12" : "right-14 bottom-12"} z-10 hidden h-10 w-10 items-center justify-center rounded-full border shadow-md transition-colors sm:flex ${fullscreen && "hidden!"}`}
            title={t("wbQrCode")}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Icon height="20" icon="mdi:qrcode" width="20" />
          </motion.button>
        </PopoverTrigger>
        <PopoverContent className="relative z-[300] w-auto p-4">
          <div className="flex flex-col items-center gap-2">
            <h3 className="text-accent-foreground text-sm font-medium">{t("wbQrCodeTitle")}</h3>
            <div className="rounded-md bg-white p-2">
              <QRCodeCanvas
                imageSettings={{
                  src: "https://public-assets.ximly.app/v3ct0rek_FO82B83D35F46_30cm_REV6%20BW.svg",
                  height: 30,
                  width: 30,
                  excavate: true,
                }}
                size={200}
                value={`${envs.VITE_SITE_URL}${location.pathname}/whiteboard`}
              />
            </div>
            <p className="text-muted-foreground text-center text-xs">{t("wbQrCodeDesc")}</p>
          </div>
        </PopoverContent>
      </Popover>

      {/* Fullscreen Whiteboard Button */}
      <motion.button
        aria-label={t("fullscreenWhiteboard" as any)}
        className={`bg-background/80 border-border text-accent-foreground hover:bg-background absolute ${fullscreen ? "right-2 bottom-2" : "right-2 bottom-12"} z-10 hidden h-10 w-10 items-center justify-center rounded-full border shadow-md transition-colors sm:flex`}
        title={t("fullscreenWhiteboard" as any)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsWhiteboardFullscreen(!isWhiteboardFullscreen)}
      >
        <Icon
          height="20"
          icon={isWhiteboardFullscreen ? "lucide:minimize" : "lucide:maximize"}
          width="20"
        />
      </motion.button>

      {isLoaded && (
        <Tldraw
          hideUi={false}
          maxAssetSize={25000000}
          overrides={{
            translations: {
              en: {
                "assets.files.amount-too-many": t("assets.files.amount-too-many"),
                "assets.files.size-too-big": t("assets.files.size-too-big"),
                "assets.files.type-not-allowed": t("assets.files.type-not-allowed"),
                "assets.files.upload-failed": t("assets.files.upload-failed"),
                "assets.url.failed": t("assets.url.failed"),
              },
            },
          }}
          store={store}
          onMount={(editor) => {
            // @ts-expect-error
            window.editor = editor;
          }}
        />
      )}
    </motion.div>
  );
}
